import { Box, Button, Typography } from '@mui/material';
import { useMemo } from 'react';
import { createPortal } from 'react-dom';

import RadarChart, { RadarChartDataSeries } from '@/components/common/RadarChart';
import { COLORS } from '@/components/dashboard/scoring';
import { Issue, IssueUrgency } from '@/models/Issue';

export interface UnveilStepProps {
  address?: string;
  onDismiss: () => void;
  /** The real condition radar series for this property (see
   * useConditionRadarSeries) — shown as a preview when present. */
  series?: RadarChartDataSeries[];
  /** Unresolved issues — summarized into urgency chips with counts when present. */
  issues?: Issue[];
  /** Mobile only — portal the CTA into this shared slot above the footer
   * instead of rendering it inline. */
  ctaSlot?: HTMLDivElement | null;
}

// Same "LABEL: count" translucent pill as HomeDNAStrip's badges
// (HomeownerHub.tsx) — matched here directly rather than through a shared
// component, since HomeDNAStrip's own badge isn't broken out into one.
const URGENCY_ORDER: { key: IssueUrgency; color: string }[] = [
  { key: IssueUrgency.Critical, color: COLORS.critical },
  { key: IssueUrgency.Urgent, color: COLORS.urgent },
  { key: IssueUrgency.Moderate, color: COLORS.moderate },
  { key: IssueUrgency.Low, color: COLORS.low },
  { key: IssueUrgency.Monitor, color: COLORS.monitor },
];

export const UnveilStep = ({ address, series, issues, onDismiss, ctaSlot }: UnveilStepProps) => {
  const urgencyCounts = useMemo(() => {
    const unresolved = (issues ?? []).filter((i) => !i.resolved);
    return URGENCY_ORDER.map(({ key, color }) => ({
      key,
      color,
      count: unresolved.filter((i) => i.urgency === key).length,
    })).filter((u) => u.count > 0);
  }, [issues]);
  const continueButton = (
    <Button
      fullWidth
      onClick={onDismiss}
      data-testid="onboarding-unveil-dismiss-btn"
      sx={{
        height: 40,
        borderRadius: '10px',
        fontWeight: 600,
        letterSpacing: '-0.02em',
        '&:hover': { backgroundColor: '#D6D3CC' },
      }}>
      View your Home HQ
    </Button>
  );

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        textAlign: 'center',
      }}>
      <Box sx={{ textAlign: 'center' }}>
        <Typography sx={{ fontSize: 22, fontWeight: 600, color: 'var(--tx-text)', lineHeight: 1.2 }}>
          Your Home HQ is ready
        </Typography>
        <Typography sx={{ fontSize: 13, color: '#918D87', mt: '4px', lineHeight: 1.5, textWrap: 'balance' }}>
          Your condition score and repair plan are in
          {address ? ` — here's what we found at ${address}.` : '.'}
        </Typography>
      </Box>

      {series && (
        <Box
          sx={{
            flex: 1,
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          {/* RadarChart's own root box carries a fixed mt:-45px/mb:-10px,
              which pulls its visible shape ~17.5px above true center —
              same asymmetry HomeScoreCard's mobile layout compensates for. */}
          <Box sx={{ transform: 'translateY(17.5px)' }}>
            <RadarChart
              series={series}
              size={280}
              showLabels={false}
              gridColor="rgba(255,255,255,0.1)"
              gridLevels={4}
            />
          </Box>
        </Box>
      )}

      {urgencyCounts.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '10px',
            mb: '20px',
          }}>
          {urgencyCounts.map(({ key, color, count }) => (
            <div
              key={key}
              style={{
                padding: '4px 12px',
                borderRadius: 6,
                fontSize: 11,
                fontWeight: 600,
                background: `${color}1a`,
                color,
                border: '1px solid transparent',
                fontFamily: "'Manrope',sans-serif",
              }}>
              {key.toUpperCase()}: {count}
            </div>
          ))}
        </Box>
      )}

      {!ctaSlot && continueButton}
      {ctaSlot && createPortal(continueButton, ctaSlot)}
    </Box>
  );
};
